import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatDateForDisplay } from '../../../utils/dateUtils';

const CustomerHistoryModal = ({ 
  isOpen = false, 
  onClose, 
  customer = null,
  appointments = []
}) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !customer) return null;

  const customerAppointments = appointments?.filter(apt => apt?.customerId === customer?.id);
  const sortedAppointments = [...customerAppointments]?.sort((a, b) => {
    const dateA = new Date(`${a.date}T${a.time}`);
    const dateB = new Date(`${b.date}T${b.time}`);
    return dateB - dateA;
  });

  return createPortal(
    <div 
      className="fixed inset-0 z-modal flex items-start justify-center pt-[8vh] px-4"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" />
      
      <div className="relative w-full max-w-3xl bg-card rounded-xl shadow-elevation-5 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 md:px-8 md:py-6 border-b border-border">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-base md:text-xl font-heading font-semibold text-primary">
                {customer?.name?.split(' ')?.map(n => n?.[0])?.join('')?.substring(0, 2)}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl md:text-2xl lg:text-h3 font-heading font-semibold text-foreground truncate">
                {customer?.name}
              </h2>
              <p className="text-sm text-muted-foreground truncate">
                Appointment History
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth flex-shrink-0"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 md:mb-8">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Icon name="Calendar" size={16} />
                <span className="text-xs font-caption">Total Visits</span>
              </div>
              <p className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                {customer?.totalVisits}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Icon name="Clock" size={16} />
                <span className="text-xs font-caption">Last Visit</span>
              </div>
              <p className="text-sm md:text-base font-data font-semibold text-foreground">
                {customer?.lastAppointment || 'Never'}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Icon name="Phone" size={16} />
                <span className="text-xs font-caption">Phone</span>
              </div>
              <p className="text-sm font-data text-foreground truncate">
                {customer?.phone}
              </p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Icon name="Mail" size={16} />
                <span className="text-xs font-caption">Email</span>
              </div>
              <p className="text-sm text-foreground truncate">
                {customer?.email}
              </p>
            </div>
          </div>

          <div className="max-h-[calc(100vh-48vh)] overflow-y-auto">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
              Appointment History ({sortedAppointments?.length})
            </h3>
            
            {sortedAppointments?.length > 0 ? (
              <div className="space-y-3">
                {sortedAppointments?.map((appointment) => (
                  <div 
                    key={appointment?.id}
                    className="bg-muted rounded-lg p-4 hover:bg-muted/80 transition-smooth"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Icon name="Calendar" size={16} className="text-primary flex-shrink-0" />
                          <span className="text-sm font-data font-semibold text-foreground">
                            {formatDateForDisplay(appointment?.date)}
                          </span>
                          <span className="text-sm font-data text-muted-foreground">
                            at {appointment?.time}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            <span className="font-data">{appointment?.duration} min</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Icon name="Briefcase" size={14} />
                            <span className="capitalize">{appointment?.service}</span>
                          </div>
                        </div>
                        {appointment?.notes && (
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {appointment?.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="px-3 py-1 bg-success/10 text-success text-xs font-caption font-medium rounded-full whitespace-nowrap">
                          Completed
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Icon name="CalendarX" size={48} className="mx-auto text-muted-foreground opacity-50 mb-4" />
                <p className="text-muted-foreground">No appointment history found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  This customer hasn't had any appointments yet
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CustomerHistoryModal;